/**
 * Piezas de los tableros de televisor.
 *
 * Un televisor se lee de lejos y sin interacción: no hay tooltips que abrir ni
 * ejes que inspeccionar. Por eso estas piezas no usan la librería de gráficos
 * del portal (pensada para el ratón) y dibujan formas simples y grandes, con el
 * dato principal siempre en cifra y la gráfica solo para dar la forma.
 *
 * DECISIÓN DE IMPLEMENTACIÓN. Las barras y celdas se construyen con HTML y
 * porcentajes, no con SVG: así el texto nunca se deforma al escalar el panel a
 * cualquier televisor. Solo los anillos y la chispa van en SVG, con trazo
 * `non-scaling-stroke` donde importa.
 */
import { Fragment, type ReactNode } from 'react';
import s from './piezas.module.css';

/* ── Tarjeta contenedora ─────────────────────────────────────────────────── */

export function Panel({
  titulo, extra, children, className = '',
}: { titulo?: string; extra?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`${s.panel} ${className}`}>
      {(titulo || extra) && (
        <header className={s.panelCabecera}>
          {titulo && <h2 className={s.panelTitulo}>{titulo}</h2>}
          {extra && <div className={s.panelExtra}>{extra}</div>}
        </header>
      )}
      <div className={s.panelCuerpo}>{children}</div>
    </section>
  );
}

/* ── Franja de indicadores ───────────────────────────────────────────────── */

export type TonoKpi = 'normal' | 'bien' | 'aviso' | 'critico';

export interface ItemKpi {
  etiqueta: string;
  valor: string | number;
  unidad?: string;
  apoyo?: ReactNode;
  tono?: TonoKpi;
  /** Serie para la silueta de fondo (tendencia del período). */
  chispa?: number[];
  /** Qué representa la chispa — sin esto la curva es un adorno indescifrable. */
  notaChispa?: string;
  /** Celda cabecera: más ancha y con la chispa del período. */
  destacado?: boolean;
}

/**
 * Los indicadores van en UNA banda continua dividida por filetes, no en cajas
 * sueltas: siete tarjetas con aire compiten entre sí y desperdician alto; una
 * franja se lee como un solo instrumento — etiqueta arriba, cifra grande
 * debajo, y el color reservado al estado (subrayado + cifra teñida). La celda
 * destacada abre la banda con la tendencia del período dibujada al fondo.
 */
export function FranjaKpis({ items }: { items: ItemKpi[] }) {
  return (
    <div className={s.franja}>
      {items.map((it) => {
        const tono = it.tono ?? 'normal';
        return (
          <div
            key={it.etiqueta}
            className={`${s.celda} ${s[`celda_${tono}`]} ${it.destacado ? s.celdaDestacada : ''}`}
          >
            {it.chispa && it.chispa.length > 1 && <Chispa datos={it.chispa} />}
            {it.chispa && it.notaChispa && <span className={s.chispaNota}>{it.notaChispa}</span>}
            <span className={s.celdaEtiqueta}>
              {tono !== 'normal' && <span className={s.celdaPunto} aria-hidden="true" />}
              {it.etiqueta}
            </span>
            <span className={s.celdaCifra}>
              <span className={s.celdaValor}>{it.valor}</span>
              {it.unidad && <span className={s.celdaUnidad}>{it.unidad}</span>}
            </span>
            {it.apoyo && <span className={s.celdaApoyo}>{it.apoyo}</span>}
          </div>
        );
      })}
    </div>
  );
}

/** Chispa de fondo: la silueta del período, sin ejes ni cifras. */
function Chispa({ datos }: { datos: number[] }) {
  const max = Math.max(1, ...datos);
  const n = datos.length;
  const pts = datos.map((v, i) => `${(i / (n - 1)) * 100},${100 - (v / max) * 100}`);
  return (
    <svg className={s.chispa} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <polygon points={`0,100 ${pts.join(' ')} 100,100`} className={s.chispaRelleno} />
      <polyline points={pts.join(' ')} className={s.chispaLinea} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ── Mapa de calor hora × categoría ──────────────────────────────────────── */

export interface FilaCalor {
  clave: string;
  etiqueta: string;
  color: string;
  /** Un valor por hora, en el mismo orden que `horas`. */
  valores: number[];
}

/**
 * Matriz categoría × hora con celdas teñidas y cifra dentro.
 *
 * Para "qué clasificación y a qué hora" gana al apilado: cada nivel tiene SU
 * fila con SU color y la hora es la columna — no hay que descomponer segmentos
 * mentalmente. La intensidad del tinte da el patrón de lejos y la cifra dentro
 * da la precisión de cerca. La intensidad se acota a 60 % para que el texto
 * siga legible sobre la celda más cargada en los dos temas.
 */
export function MapaCalorHora({ horas, filas }: { horas: string[]; filas: FilaCalor[] }) {
  const max = Math.max(1, ...filas.flatMap((f) => f.valores));
  return (
    <div
      className={s.mapa}
      style={{
        // `auto`: la columna de etiquetas mide lo que su nombre más largo — un
        // ancho fijo truncaba «No pudieron entrar» en los paneles a media
        // pantalla. Las etiquetas son un conjunto conocido y corto, así que no
        // hay riesgo de que la columna se coma el mapa.
        gridTemplateColumns: `auto repeat(${horas.length}, 1fr)`,
        gridTemplateRows: `repeat(${filas.length}, minmax(0, 1fr)) auto`,
      }}
    >
      {filas.map((f) => {
        const total = f.valores.reduce((a, b) => a + b, 0);
        return (
          <Fragment key={f.clave}>
            <span className={s.mapaEtiqueta}>
              <span className={s.mapaNombre}>
                <span className={s.mapaMarca} style={{ background: f.color }} />
                {f.etiqueta}
              </span>
              <b className={s.mapaTotal}>{total}</b>
            </span>
            {f.valores.map((v, i) => (
              <span
                key={i}
                className={s.mapaCelda}
                style={v > 0
                  ? { background: `color-mix(in srgb, ${f.color} ${Math.round(16 + 44 * (v / max))}%, transparent)` }
                  : undefined}
                title={`${horas[i]}:00 · ${f.etiqueta}: ${v}`}
              >
                {v > 0 ? v : ''}
              </span>
            ))}
          </Fragment>
        );
      })}
      <span aria-hidden="true" />
      {horas.map((h) => <span key={h} className={s.mapaHora}>{h}</span>)}
    </div>
  );
}

/* ── Líneas por hora ─────────────────────────────────────────────────────── */

export interface SerieLinea {
  clave: string;
  etiqueta: string;
  color: string;
  /** Un valor por hora, en el mismo orden que `horas`. */
  valores: number[];
}

/** Traza suavizada (Catmull-Rom → Bézier) sobre un lienzo 0-100 × 0-100. */
function trazo(valores: number[], max: number): string {
  const n = valores.length;
  if (n === 0) return '';
  const px = (i: number) => (n > 1 ? (i / (n - 1)) * 100 : 50);
  const py = (v: number) => 100 - (v / max) * 96 - 2; // 2 % de margen arriba/abajo
  let d = `M ${px(0)} ${py(valores[0])}`;
  for (let i = 0; i < n - 1; i++) {
    const cx = (px(i) + px(i + 1)) / 2;
    d += ` C ${cx} ${py(valores[i])}, ${cx} ${py(valores[i + 1])}, ${px(i + 1)} ${py(valores[i + 1])}`;
  }
  return d;
}

/**
 * Varias series sobre el mismo eje horario.
 *
 * Para magnitudes ANIDADAS (finalizadas ⊆ entraron ⊆ solicitadas) apilar
 * mentiría — se leería como suma — y agrupar barras por hora daría palillos
 * ilegibles a distancia. Las líneas dejan ver a la vez la forma de cada serie
 * y la brecha entre ellas, que es justo el dato: cuánta demanda se pierde en
 * cada paso. Un solo eje, sin cifras por punto; la leyenda identifica y los
 * totales van en ella para no depender del color.
 */
export function LineasHora({ horas, series }: { horas: string[]; series: SerieLinea[] }) {
  const max = Math.max(1, ...series.flatMap((se) => se.valores));
  const hayDatos = series.some((se) => se.valores.some((v) => v > 0));

  return (
    <div className={s.lineas}>
      <div className={s.leyenda}>
        {series.map((se) => (
          <span key={se.clave} className={s.leyendaItem}>
            <span className={s.leyendaMarca} style={{ background: se.color }} />
            {se.etiqueta}
            <b className={s.leyendaTotal}>{se.valores.reduce((a, b) => a + b, 0)}</b>
          </span>
        ))}
      </div>
      <div className={s.lienzoCurvas}>
        {!hayDatos && <span className={s.sinDatosCentrado}>Sin datos en el período</span>}
        <svg className={s.curvas} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {/* Rejilla al 25/50/75 %: referencia de altura sin ejes numerados. */}
          {[25, 50, 75].map((y) => (
            <line key={y} x1="0" y1={y} x2="100" y2={y} className={s.rejilla} vectorEffect="non-scaling-stroke" />
          ))}
          {hayDatos && series.map((se) => (
            <path
              key={se.clave}
              d={trazo(se.valores, max)}
              fill="none"
              stroke={se.color}
              strokeWidth="2.5"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
        </svg>
      </div>
      <div className={s.ejeHoras}>
        {horas.map((h, i) => (
          <span key={h} className={s.ejeHora}>{i % 2 === 0 ? h : ''}</span>
        ))}
      </div>
    </div>
  );
}

/* ── Ranking con barras apiladas ─────────────────────────────────────────── */

export interface SerieRanking {
  clave: string;
  etiqueta: string;
  color: string;
}

/**
 * Una fila por entidad (aquí, médicos) con barra apilada y total a la derecha.
 *
 * La barra se escala contra el máximo del grupo: la comparación entre filas es
 * el punto del gráfico. El rótulo directo por fila evita depender del color
 * para saber de quién se habla; la leyenda solo desambigua los segmentos.
 */
export function RankingApilado({ series, filas, tope = 8, textoVacio, anchoEtiqueta = '10em' }: {
  series: SerieRanking[];
  /** `valores` alineado con `series`, en el mismo orden. */
  filas: { clave: string; etiqueta: string; valores: number[] }[];
  /** Máximo de filas: en un televisor, más filas = letras más chicas. */
  tope?: number;
  textoVacio?: string;
  /**
   * Ancho de la columna de etiquetas. Se puede ampliar en los rankings cuyos
   * nombres son largos —una zona rotulada "Fuera de Panamá · Costa Rica" se
   * cortaba justo en el país, que es el dato— sin encoger las barras del resto
   * de los tableros, que viven bien con el ancho por defecto.
   */
  anchoEtiqueta?: string;
}) {
  const conDatos = filas
    .map((f) => ({ ...f, total: f.valores.reduce((a, b) => a + b, 0) }))
    .filter((f) => f.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, tope);
  if (conDatos.length === 0) {
    return <span className={s.sinDatosCentrado}>{textoVacio ?? 'Sin datos en el período'}</span>;
  }
  const max = Math.max(1, ...conDatos.map((f) => f.total));

  return (
    <div className={s.rank}>
      <div className={s.leyenda}>
        {series.map((se) => (
          <span key={se.clave} className={s.leyendaItem}>
            <span className={s.leyendaMarca} style={{ background: se.color }} />
            {se.etiqueta}
          </span>
        ))}
      </div>
      <div className={s.rankFilas}>
        {conDatos.map((f) => (
          <div
            key={f.clave}
            className={s.rankFila}
            style={{ gridTemplateColumns: `minmax(0, ${anchoEtiqueta}) 1fr auto` }}
          >
            <span className={s.rankEtiqueta} title={f.etiqueta}>{f.etiqueta}</span>
            <div className={s.rankPista}>
              <div className={s.rankBarra} style={{ width: `${(f.total / max) * 100}%` }}>
                {f.valores.map((v, i) => (
                  v > 0
                    ? <div key={series[i].clave} className={s.rankSegmento} style={{ flexGrow: v, background: series[i].color }} title={`${f.etiqueta} · ${series[i].etiqueta}: ${v}`} />
                    : null
                ))}
              </div>
            </div>
            <b className={s.rankTotal}>{f.total}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Banda de ocupación ──────────────────────────────────────────────────── */

export interface SegmentoBanda {
  clave: string;
  etiqueta: string;
  valor: number;
  color: string;
}

/**
 * Ocupación contra una capacidad, en UNA sola banda apilada.
 *
 * Numerador y denominador tienen que verse juntos: separar "cuánto hay" de
 * "cuánto cabe" en dos gráficos obliga a hacer la división de cabeza. Aquí la
 * banda completa ES la capacidad de este momento, los segmentos son lo que la
 * está usando, y el hueco restante es lo que queda libre.
 */
export function BandaOcupacion({ segmentos, capacidad, etiquetaLibre = 'Libres', textoVacio, cola, notaPie }: {
  /** Lo que OCUPA un puesto ahora mismo. La cola NO va aquí: ver `cola`. */
  segmentos: SegmentoBanda[];
  /** Puestos que pueden atenderse EN SIMULTÁNEO en este momento. */
  capacidad: number;
  etiquetaLibre?: string;
  textoVacio?: string;
  /**
   * Personas esperando. Se muestra al lado, nunca dentro del porcentaje.
   *
   * Antes la cola se sumaba a los ocupados y el resultado llegó a marcar 360 %:
   * quien espera no está ocupando un puesto, y meterla en el numerador vuelve
   * el indicador ilimitado — una fila larga con pocos médicos disparaba la
   * cifra sin que la ocupación real cambiara. Son dos magnitudes distintas:
   * una dice cuánto de mi servicio está en uso, la otra cuánta gente lo espera.
   */
  cola?: number;
  /** Contexto extra bajo la banda (p. ej. la capacidad del horizonte). */
  notaPie?: string;
}) {
  const usados = segmentos.reduce((a, x) => a + x.valor, 0);
  if (capacidad <= 0 && usados <= 0) {
    return <span className={s.sinDatosCentrado}>{textoVacio ?? 'Sin capacidad configurada'}</span>;
  }
  // La sobrecarga existe: si hay más gente que cupos, la banda representa a la
  // gente y el segmento libre simplemente desaparece. Con la cola fuera del
  // numerador esto ya solo ocurre por una causa real —una consulta sigue
  // activa con su médico desconectado— y no por el tamaño de la fila.
  const escala = Math.max(capacidad, usados, 1);
  const libres = Math.max(0, capacidad - usados);
  const pct = Math.min(999, Math.round((usados / Math.max(capacidad, 1)) * 100));

  return (
    <div className={s.banda}>
      <div className={s.bandaCabecera}>
        <span className={s.bandaPct}>{pct}<span className={s.bandaPctSigno}>%</span></span>
        <div className={s.bandaTexto}>
          <span className={s.bandaLabel}>ocupación de la capacidad</span>
          <span className={s.bandaSub}>
            {usados} de {capacidad} puestos en atención simultánea
            {typeof cola === 'number' && cola > 0 && ` · ${cola} en fila`}
          </span>
        </div>
      </div>

      <div className={s.bandaPista}>
        {segmentos.map((x) => (
          x.valor > 0
            ? <div key={x.clave} className={s.bandaSegmento} style={{ flexGrow: x.valor / escala, background: x.color }} title={`${x.etiqueta}: ${x.valor}`} />
            : null
        ))}
        {libres > 0 && <div className={s.bandaSegmentoLibre} style={{ flexGrow: libres / escala }} />}
      </div>

      <ul className={s.bandaLista}>
        {segmentos.map((x) => (
          <li key={x.clave} className={s.bandaFila}>
            <span className={s.leyendaMarca} style={{ background: x.color }} />
            <span className={s.bandaFilaEtiqueta}>{x.etiqueta}</span>
            <b className={s.bandaFilaValor}>{x.valor}</b>
          </li>
        ))}
        <li className={s.bandaFila}>
          <span className={`${s.leyendaMarca} ${s.marcaLibre}`} />
          <span className={s.bandaFilaEtiqueta}>{etiquetaLibre}</span>
          <b className={s.bandaFilaValor}>{libres}</b>
        </li>
        {/* La fila va FUERA de la banda: es demanda esperando, no ocupación. */}
        {typeof cola === 'number' && cola > 0 && (
          <li className={`${s.bandaFila} ${s.bandaFilaCola}`}>
            <span className={`${s.leyendaMarca} ${s.marcaCola}`} />
            <span className={s.bandaFilaEtiqueta}>En fila (no ocupan puesto)</span>
            <b className={s.bandaFilaValor}>{cola}</b>
          </li>
        )}
      </ul>
      {notaPie && <span className={s.bandaNotaPie}>{notaPie}</span>}
    </div>
  );
}

/* ── Dona de proporción ──────────────────────────────────────────────────── */

export interface GajoDona {
  clave: string;
  etiqueta: string;
  valor: number;
  color: string;
}

/**
 * Anillo segmentado con una cifra al centro y el desglose al lado.
 *
 * Con pocas categorías (aquí nunca pasan de seis) la dona comunica la
 * proporción de un vistazo mejor que una pila de barras, y la lista adjunta —
 * marca de color, etiqueta, cifra y porcentaje — carga con la precisión que el
 * anillo solo no da. Los gajos se separan con un pequeño hueco para que dos
 * colores vecinos no se fundan a distancia.
 *
 * El centro muestra el total de los gajos salvo que `valorCentro` diga otra
 * cosa — p. ej. en Médicos el anillo reparte libres/ocupados/desconectados
 * pero la cifra que importa es cuántos están EN LÍNEA.
 */
export function DonaResumen({ gajos, etiquetaCentro, valorCentro, textoVacio }: {
  gajos: GajoDona[];
  etiquetaCentro: string;
  valorCentro?: ReactNode;
  textoVacio?: string;
}) {
  const total = gajos.reduce((acc, g) => acc + g.valor, 0);
  if (total === 0) {
    return <span className={s.sinDatosCentrado}>{textoVacio ?? 'Sin datos en el período'}</span>;
  }
  const R = 44;
  const C = 2 * Math.PI * R;
  const HUECO = total > 1 && gajos.filter((g) => g.valor > 0).length > 1 ? 2.4 : 0;
  return (
    <div className={s.dona}>
      <div className={s.donaAnillo}>
        <svg viewBox="0 0 120 120" className={s.donaSvg}>
          {gajos
            .filter((g) => g.valor > 0)
            .reduce((acc, g) => {
              const frac = g.valor / total;
              const largo = Math.max(0, frac * C - HUECO);
              const currentOffset = acc.offset;
              acc.items.push({ ...g, frac, largo, currentOffset });
              acc.offset += frac * C;
              return acc;
            }, { items: [] as (Omit<typeof gajos[0], 'valor'> & { valor: number; frac: number; largo: number; currentOffset: number })[], offset: 0 })
            .items
            .map((g) => {
              const { largo, currentOffset } = g;
            return (
              <circle
                key={g.clave}
                cx="60" cy="60" r={R}
                className={s.donaGajo}
                stroke={g.color}
                strokeDasharray={`${largo} ${C - largo}`}
                strokeDashoffset={-currentOffset}
                transform="rotate(-90 60 60)"
              />
            );
          })}
        </svg>
        <div className={s.donaCentro}>
          <span className={s.donaTotal}>{valorCentro ?? total}</span>
          <span className={s.donaCentroEtiqueta}>{etiquetaCentro}</span>
        </div>
      </div>

      <ul className={s.donaLista}>
        {gajos.filter((g) => g.valor > 0).map((g) => (
          <li key={g.clave} className={s.donaFila}>
            <span className={s.donaMarca} style={{ background: g.color }} />
            <span className={s.donaEtiqueta}>{g.etiqueta}</span>
            <b className={s.donaValor}>{g.valor}</b>
            <span className={s.donaPct}>{Math.round((g.valor / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Embudo / etapas ─────────────────────────────────────────────────────── */

export function Etapas({ etapas }: {
  etapas: { clave: string; etiqueta: string; valor: number; color: string }[];
}) {
  const max = Math.max(1, ...etapas.map((e) => e.valor));
  return (
    <div className={s.etapas}>
      {etapas.map((e) => (
        <div key={e.clave} className={s.etapaFila}>
          <span className={s.etapaEtiqueta}>{e.etiqueta}</span>
          <div className={s.etapaPista}>
            <div className={s.etapaBarra} style={{ width: `${(e.valor / max) * 100}%`, background: e.color }} />
          </div>
          <span className={s.etapaValor}>{e.valor}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Alertas ─────────────────────────────────────────────────────────────── */

export interface Alerta {
  id: string;
  nivel: 'critico' | 'aviso' | 'info';
  texto: string;
  detalle?: string;
}

export function ListaAlertas({ alertas }: { alertas: Alerta[] }) {
  if (alertas.length === 0) {
    return (
      <div className={s.sinAlertas}>
        <span className={s.sinAlertasMarca} aria-hidden="true" />
        Sin alertas · operación normal
      </div>
    );
  }
  return (
    <ul className={s.alertas}>
      {alertas.map((a) => (
        <li key={a.id} className={`${s.alerta} ${s[`alerta_${a.nivel}`]}`}>
          <span className={s.alertaPunto} aria-hidden="true" />
          <div className={s.alertaCuerpo}>
            <p className={s.alertaTexto}>{a.texto}</p>
            {a.detalle && <p className={s.alertaDetalle}>{a.detalle}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ── Tabla compacta ──────────────────────────────────────────────────────── */

export function TablaTv({ columnas, filas }: {
  columnas: { clave: string; titulo: string; numerica?: boolean }[];
  filas: Record<string, ReactNode>[];
}) {
  return (
    <table className={s.tabla}>
      <thead>
        <tr>
          {columnas.map((c) => (
            <th key={c.clave} className={c.numerica ? s.num : undefined}>{c.titulo}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {filas.map((f, i) => (
          <tr key={i}>
            {columnas.map((c) => (
              <td key={c.clave} className={c.numerica ? s.num : undefined}>{f[c.clave]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
